import { useCallback } from 'react';
import styled from 'foundations-theming/utils';
import { EARTH_LAYER } from 'foundations-components/transitional/constants';
import { LARGE_BUTTON_WIDTH, MEDIUM_BUTTON_WIDTH } from '../constants/ButtonConstants';
import ButtonLabel from './ButtonLabel';
import { LG, MD, SM } from 'crm-activity-creator-data/types/ActivityButtonProps';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { View } from 'foundations-components/components/View';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';

// @todo: Remove this once the icon size is updated https://git.hubteam.com/HubSpot/foundations-assets/issues/212
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCircleIconWrapper = styled.div.withConfig({
  displayName: "LabelledIconButton__StyledCircleIconWrapper",
  componentId: "sc-1hiq1v8-0"
})(["position:absolute;top:-4px;right:", ";z-index:", ";font-size:8px;"], props => props.buttonSize === LG ? '6px' : '0', EARTH_LAYER);
function LabelledIconButton({
  ariaLabel,
  buttonSize = MD,
  dataTestId,
  disabled = false,
  hasUpgradePath = false,
  IconComponent,
  iconName,
  isError = false,
  isPopoverOpen = false,
  label,
  onboardingSelector,
  onClick,
  seleniumSelector,
  tooltip
}) {
  const handleClick = useCallback(() => {
    if (disabled) {
      return null;
    }
    return onClick();
  }, [disabled, onClick]);
  const styledCircleIconProps = isError ? {
    name: 'warning',
    icon: WarningIcon,
    backgroundColor: 'icon-alert-default',
    color: 'icon-core-on-fill-default',
    borderColor: 'icon-alert-default'
  } : hasUpgradePath ? {
    name: 'locked',
    icon: LockedIcon,
    backgroundColor: 'fill-accent-neutral-default',
    color: 'icon-core-on-fill-default',
    borderColor: 'fill-accent-neutral-default'
  } : undefined;
  return /*#__PURE__*/_jsx(View, {
    as: "span",
    display: "block",
    "aria-disabled": disabled,
    children: /*#__PURE__*/_jsx(Tooltip, {
      "data-test-id": "activity-button-tooltip",
      title: tooltip,
      maxInlineSize: "240px",
      tabIndex: disabled ? 0 : -1,
      children: /*#__PURE__*/_jsxs(View, {
        as: "div",
        textAlign: "center",
        display: "block",
        position: "relative",
        inlineSize: `${buttonSize === LG ? LARGE_BUTTON_WIDTH : MEDIUM_BUTTON_WIDTH}px`,
        marginInline: buttonSize === SM ? '0' : '100',
        backgroundColor: "fill-transparent-default",
        tabIndex: -1,
        children: [styledCircleIconProps && /*#__PURE__*/_jsx(StyledCircleIconWrapper, Object.assign({
          buttonSize: buttonSize
        }, styledCircleIconProps, {
          children: /*#__PURE__*/_jsx(IconCircle, {
            icon: styledCircleIconProps.icon,
            legacy: true,
            backgroundColor: styledCircleIconProps.backgroundColor,
            color: styledCircleIconProps.color,
            borderColor: styledCircleIconProps.borderColor
          })
        })), /*#__PURE__*/_jsx(IconButton, {
          "aria-label": ariaLabel,
          marginBlockEnd: "100",
          paddingInline: "0",
          shape: "circle",
          size: buttonSize === LG ? 'medium' : 'small',
          "data-onboarding": onboardingSelector,
          "data-selenium-test": seleniumSelector,
          "data-test-id": dataTestId,
          onClick: handleClick,
          disabled: disabled,
          tabIndex: 0,
          "data-dropdown-open": isPopoverOpen // Used only for selenium testing to replicate behavior of old UIButton component (test-events-activity-buttons tests in crm-records-ui)
          ,
          children: /*#__PURE__*/_jsx(IconComponent, {
            "data-test-id": `activity-button-icon-${iconName}`
          })
        }), /*#__PURE__*/_jsx(ButtonLabel, {
          disabled: disabled,
          text: label
        })]
      })
    })
  });
}
export default LabelledIconButton;